import { computed, reactive, ref, watch, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import type { JSONContent } from '@tiptap/vue-3'
import usePost from '@/core/composables/api/usePost'
import { ApiRequestStatus } from '@/core/models/api/api-request-status'
import { FeedbackTopicType } from '@/core/models/feedback-topic'
import { useFeedbackTopicStore } from '@/stores/feedback-topic.store'
import { useSnackStore } from '@/stores/snack.store'
import { useEventBus } from '@vueuse/core'
import { EventKey } from '@/core/events/event-key'
import type { GatheringEntry, Perspective } from '@/core/models/perspective'
import {
  emptyPerspectiveGatheringState,
  type PerspectiveGatheringState,
} from '@/core/models/perspective'

export enum SurveyRecurrenceFrequency {
  Week = 'week',
  Month = 'month',
}

const START_ASSESSMENT_STEPS = {
  selectMembersAndTemplate: 'select-members-and-template',
  selectQuestionGroups: 'select-question-groups',
  selectPerspectives: 'select-perspectives',
  addMetadata: 'add-metadata',
} as const

type StartAssessmentStep = (typeof START_ASSESSMENT_STEPS)[keyof typeof START_ASSESSMENT_STEPS]

export const useStartAssessment = (
  isOpen: Ref<boolean>,
  onCreated?: (assessmentId: string) => void,
) => {
  const { t } = useI18n()
  const snackStore = useSnackStore()

  const feedbackTopicStore = useFeedbackTopicStore()
  const { templates, loadingStatus: templateLoadingStatus } = storeToRefs(feedbackTopicStore)

  const assessmentCreatedBus = useEventBus(EventKey.assessmentCreated)

  const form = reactive<{
    title?: string
    message?: JSONContent
    feedbackTopicId?: string
    coacheeIds: string[]
    includeSelfFeedback: boolean
    questionGroupIds: string[]
    perspectiveGathering: Record<string, PerspectiveGatheringState>
  }>({
    title: undefined,
    message: undefined,
    feedbackTopicId: undefined,
    coacheeIds: [],
    includeSelfFeedback: false,
    questionGroupIds: [],
    perspectiveGathering: {},
  })

  const errors = ref<ErrorMap>(new Map())
  const stepIndex = ref(0)

  const filteredTemplates = computed(() =>
    templates.value.filter((template) => template.type === FeedbackTopicType.Evaluation),
  )

  const selectedTemplate = computed(() =>
    filteredTemplates.value.find((template) => template.id === form.feedbackTopicId),
  )

  const includeSelfAvailable = computed(() => selectedTemplate.value?.includeSelf ?? false)

  /** Perspectives that support in-app gathering (app_user). */
  const availablePerspectives = computed((): Perspective[] => {
    if (!selectedTemplate.value) return []
    return selectedTemplate.value.perspectives
      .filter((p) => p.allowedMechanisms.includes('app_user'))
      .map((p) => ({
        ...p,
        allowedMechanisms: [...p.allowedMechanisms],
      }))
  })

  const selfRequired = computed(
    () =>
      !!selectedTemplate.value?.includeSelf && selectedTemplate.value.perspectives.length === 0,
  )

  const requiredPerspectiveKeys = computed(() => {
    const keys = new Set<string>()
    if (
      selectedTemplate.value &&
      !selectedTemplate.value.includeSelf &&
      selectedTemplate.value.perspectives.length === 1
    ) {
      keys.add(selectedTemplate.value.perspectives[0].key)
    }
    return keys
  })

  const ensurePerspectiveState = (key: string) => {
    if (!form.perspectiveGathering[key]) {
      form.perspectiveGathering[key] = emptyPerspectiveGatheringState()
    }
    return form.perspectiveGathering[key]
  }

  watch(
    availablePerspectives,
    (perspectives) => {
      const keys = new Set(perspectives.map((p) => p.key))
      for (const key of Object.keys(form.perspectiveGathering)) {
        if (!keys.has(key)) {
          delete form.perspectiveGathering[key]
        }
      }
      for (const perspective of perspectives) {
        const state = ensurePerspectiveState(perspective.key)
        if (requiredPerspectiveKeys.value.has(perspective.key)) {
          state.enabled = true
        }
        if (
          perspective.allowedMechanisms.length === 1 &&
          perspective.allowedMechanisms[0] === 'link'
        ) {
          state.useOpenLink = true
        }
      }
    },
    { immediate: true },
  )

  watch(selfRequired, (required) => {
    if (required) {
      form.includeSelfFeedback = true
    }
  })

  watch(includeSelfAvailable, (available) => {
    if (!available) {
      form.includeSelfFeedback = false
    }
  })

  const buildGathering = (): GatheringEntry[] => {
    const gathering: GatheringEntry[] = []

    for (const perspective of availablePerspectives.value) {
      const state = form.perspectiveGathering[perspective.key]
      if (!state?.enabled) continue

      if (
        perspective.allowedMechanisms.includes('app_user') &&
        state.appUserMemberIds.length > 0
      ) {
        gathering.push({
          perspectiveKey: perspective.key,
          mechanism: 'app_user',
          memberIds: [...state.appUserMemberIds],
          enabled: true,
        })
      }
      if (state.useOpenLink && perspective.allowedMechanisms.includes('link')) {
        gathering.push({
          perspectiveKey: perspective.key,
          mechanism: 'link',
          enabled: true,
        })
      }
    }

    return gathering
  }

  const configurableTemplates = computed(() => {
    if (
      selectedTemplate.value &&
      selectedTemplate.value.questionGroupSections &&
      selectedTemplate.value.questionGroupSections.filter((section) => !section.required).length > 0
    ) {
      return [selectedTemplate.value]
    }
    return []
  })

  const isConfigurable = computed(() => configurableTemplates.value.length > 0)

  const steps = computed<StartAssessmentStep[]>(() => [
    START_ASSESSMENT_STEPS.selectMembersAndTemplate,
    ...(isConfigurable.value ? [START_ASSESSMENT_STEPS.selectQuestionGroups] : []),
    START_ASSESSMENT_STEPS.selectPerspectives,
    START_ASSESSMENT_STEPS.addMetadata,
  ])

  const step = computed(() => steps.value[stepIndex.value])
  const isFirstStep = computed(() => stepIndex.value === 0)
  const isLastStep = computed(() => stepIndex.value === steps.value.length - 1)

  const validatePerspectiveGathering = () => {
    for (const perspective of availablePerspectives.value) {
      const state = form.perspectiveGathering[perspective.key]
      if (!state?.enabled) continue

      const hasAppUser =
        perspective.allowedMechanisms.includes('app_user') && state.appUserMemberIds.length > 0
      const hasOpenLink = state.useOpenLink && perspective.allowedMechanisms.includes('link')

      if (requiredPerspectiveKeys.value.has(perspective.key) && !hasAppUser && !hasOpenLink) {
        errors.value.set(perspective.key, t('feedback.formValidation.members'))
      }
    }
  }

  const validateStep = () => {
    errors.value.clear()

    switch (step.value) {
      case START_ASSESSMENT_STEPS.selectMembersAndTemplate:
        if (form.coacheeIds.length === 0) {
          errors.value.set('coacheeIds', t('feedback.formValidation.members'))
        }
        if (!form.feedbackTopicId) {
          errors.value.set('feedbackTopicId', t('feedback.formValidation.templates'))
        }
        break
      case START_ASSESSMENT_STEPS.selectQuestionGroups:
        if (
          form.questionGroupIds.length === 0 &&
          !configurableTemplates.value.some((template) =>
            template.questionGroupSections?.some((section) => section.required),
          )
        ) {
          errors.value.set('questionGroupIds', t('feedback.formValidation.questionGroups'))
        }
        break
      case START_ASSESSMENT_STEPS.selectPerspectives: {
        const gathering = buildGathering()
        const hasSelf = form.includeSelfFeedback && includeSelfAvailable.value
        if (gathering.length === 0 && !hasSelf) {
          errors.value.set('perspectives', t('startSurvey.gatheringRequired'))
          break
        }
        validatePerspectiveGathering()

        if (selectedTemplate.value) {
          const hasPerspective = selectedTemplate.value.perspectives.some((p) => {
            const state = form.perspectiveGathering[p.key]
            return state?.enabled
          })
          if (!hasSelf && !hasPerspective) {
            errors.value.set(
              'perspectives',
              t('startSurvey.perspectivesRequired', {
                templateTitle: selectedTemplate.value.title,
              }),
            )
          }
        }
        break
      }
    }
  }

  const nextStep = () => {
    validateStep()
    if (errors.value.size === 0 && !isLastStep.value) {
      stepIndex.value++
    }
  }

  const previousStep = () => {
    if (stepIndex.value > 0) {
      stepIndex.value--
    }
    errors.value.clear()
  }

  const clearForm = () => {
    errors.value.clear()
    form.title = undefined
    form.message = undefined
    form.feedbackTopicId = undefined
    form.coacheeIds = []
    form.includeSelfFeedback = false
    form.questionGroupIds = []
    form.perspectiveGathering = {}
    stepIndex.value = 0
  }

  watch(
    form,
    () => {
      errors.value.clear()
    },
    { deep: true },
  )

  watch(isOpen, (newValue) => {
    if (!newValue) {
      clearForm()
    }
  })

  const getTemplateQuestionGroupIds = (templateId?: string) => {
    if (!templateId) return []
    return (
      filteredTemplates.value
        .find((template) => template.id === templateId)
        ?.questionGroupSections?.flatMap((section) => section.questionGroups)
        .map((questionGroup) => questionGroup.id) ?? []
    )
  }

  watch(
    () => form.feedbackTopicId,
    (templateId, previousTemplateId) => {
      const addedQuestionGroupIds = getTemplateQuestionGroupIds(templateId)
      form.questionGroupIds = Array.from(new Set([...form.questionGroupIds, ...addedQuestionGroupIds]))

      const removedQuestionGroupIds = new Set(getTemplateQuestionGroupIds(previousTemplateId))
      form.questionGroupIds = form.questionGroupIds.filter((id) => !removedQuestionGroupIds.has(id))
    },
  )

  watch(steps, (newSteps) => {
    if (stepIndex.value >= newSteps.length) {
      stepIndex.value = Math.max(0, newSteps.length - 1)
    }
  })

  const {
    mutate: createAssessment,
    status: createSurveyStatus,
    data: createdSurveyId,
  } = usePost<string, StartAssessmentPayload>('assessment')

  const saveSurvey = async () => {
    validateStep()
    if (errors.value.size > 0) {
      return
    }

    await createAssessment({
      title: form.title,
      message: form.message,
      feedbackTopicId: form.feedbackTopicId,
      coacheeIds: form.coacheeIds,
      includeSelfFeedback: form.includeSelfFeedback && includeSelfAvailable.value,
      questionGroupIds: form.questionGroupIds,
      gathering: buildGathering(),
    })

    switch (createSurveyStatus.value) {
      case ApiRequestStatus.Success:
        if (onCreated) {
          onCreated(createdSurveyId.value)
        }

        snackStore.showInfo(t('feedback.requestFeedbackSuccess'))
        assessmentCreatedBus.emit({ assessmentId: createdSurveyId.value })
        break
      case ApiRequestStatus.Error:
        snackStore.showError(t('feedback.requestFeedbackError'))
        break
    }
  }

  return {
    step,
    isFirstStep,
    isLastStep,
    nextStep,
    previousStep,
    saveSurvey,
    form,
    errors,
    filteredTemplates,
    configurableTemplates,
    availablePerspectives,
    includeSelfAvailable,
    selfRequired,
    requiredPerspectiveKeys,
    ensurePerspectiveState,
    clearForm,
    templateLoadingStatus,
  }
}

export type StartAssessmentPayload = {
  title?: string
  message?: JSONContent
  feedbackTopicId?: string
  coacheeIds: string[]
  includeSelfFeedback: boolean
  questionGroupIds: string[]
  gathering: GatheringEntry[]
}

/** @deprecated Prefer StartAssessmentPayload; kept for edit-assessment until that flow migrates. */
export type StartAssessmentFormData = {
  title?: string
  message?: JSONContent
  feedbackTopicId?: string
  coacheeIds: string[]
  peerIds: string[]
  includeSelfFeedback: boolean
  questionGroupIds: string[]
}

export type ErrorMap = Map<string, string>
